import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { supabase } from '@/integrations/supabase/client';
import { getSafeSessionStorage, isRestrictedWebView } from '@/lib/safeStorage';

const safeSession = getSafeSessionStorage();

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface PWAContextValue {
  isInstallable: boolean;
  isInstalled: boolean;
  updateAvailable: boolean;
  setUpdateAvailable: (v: boolean) => void;
  installApp: () => Promise<boolean>;
  updateApp: () => Promise<void>;
}

const PWAContext = createContext<PWAContextValue | null>(null);

export function PWAProvider({ children }: { children: ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const suppressUpdateRef = useRef(false);
  const reloadingRef = useRef(false);

  useEffect(() => {
    // Suppress update banner for 10s after a fresh update
    try {
      const justUpdated = safeSession.getItem('pwa-just-updated');
      if (justUpdated) {
        suppressUpdateRef.current = true;
        safeSession.removeItem('pwa-just-updated');
        setTimeout(() => { suppressUpdateRef.current = false; }, 10000);
      }
    } catch { /* ignore restricted storage */ }


    // Detect standalone (installed) mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) setIsInstalled(true);

    // Register SW exactly ONCE via PWAContext. Skip on restricted in-app
    // webviews (WhatsApp/Instagram/Facebook on Android) where SW registration
    // can throw synchronously and blank the page.
    let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined;
    if (!isRestrictedWebView() && 'serviceWorker' in navigator) {
      try {
        updateSW = registerSW({
      onNeedRefresh() {
        console.log('[PWA] New version available');
        if (!suppressUpdateRef.current) {
          setUpdateAvailable(true);
        }
      },
      onOfflineReady() {
        console.log('[PWA] App ready for offline use');
      },
      onRegisteredSW(swUrl, registration) {
        console.log('[PWA] SW registered:', swUrl);
        if (!registration) return;
        registrationRef.current = registration;

        // 1) If a SW is ALREADY waiting at registration time, surface the banner.
        if (registration.waiting && navigator.serviceWorker.controller) {
          console.log('[PWA] SW already waiting on load → prompt update');
          if (!suppressUpdateRef.current) setUpdateAvailable(true);
        }

        // 2) Listen for newly found SWs and watch their state transitions.
        //    This is more reliable than relying solely on onNeedRefresh in
        //    some browsers/edge cases (e.g. when the page was opened from
        //    cache and the SW transitions installed→waiting very fast).
        const trackInstalling = (sw: ServiceWorker | null) => {
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[PWA] New SW installed and waiting → prompt update');
              if (!suppressUpdateRef.current) setUpdateAvailable(true);
            }
          });
        };
        trackInstalling(registration.installing);
        registration.addEventListener('updatefound', () => {
          trackInstalling(registration.installing);
        });

        // 3) Periodic update check every 60s (only while tab is open).
        setInterval(async () => {
          if (registration.installing) return;
          if ('connection' in navigator && !navigator.onLine) return;
          try {
            const resp = await fetch(swUrl, {
              cache: 'no-store',
              headers: { 'Cache-Control': 'no-cache' },
            });
            if (resp?.status === 200) await registration.update();
          } catch {
            // Network unavailable, skip
          }
        }, 60 * 1000);
      },
        });
      } catch (err) {
        console.warn('[PWA] SW registration skipped:', err);
      }
    }
    void updateSW;

    // On visibility change → trigger registration.update() directly (not updateSW(false))
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible' && !suppressUpdateRef.current) {
        const reg = registrationRef.current;
        if (reg) {
          try {
            await reg.update();
            // If there's already a waiting SW, show the banner
            if (reg.waiting) {
              setUpdateAvailable(true);
            }
          } catch {
            // offline, ignore
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // controllerchange → the new SW has taken control → reload the page
    const handleControllerChange = () => {
      if (!reloadingRef.current) {
        reloadingRef.current = true;
        window.location.reload();
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    // beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Subscribe to Supabase Realtime broadcast for force-update
    const channel = supabase.channel('app-updates').on(
      'broadcast',
      { event: 'force-update' },
      async () => {
        console.log('[PWA] Force-update broadcast received');
        const reg = registrationRef.current;
        if (reg) {
          try {
            await reg.update();
            if (reg.waiting) {
              try { safeSession.setItem('pwa-just-updated', '1'); } catch {}
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
              return;
            }
          } catch { /* offline */ }
        }
        // Fallback: hard reload
        if (!reloadingRef.current) {
          reloadingRef.current = true;
          window.location.reload();
        }
      }
    ).subscribe();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      supabase.removeChannel(channel);
    };
  }, []);

  const installApp = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      }
    } catch (err) {
      console.error('[PWA] Install prompt error:', err);
    }
    return false;
  }, [deferredPrompt]);

  const updateApp = useCallback(async (): Promise<void> => {
    try { safeSession.setItem('pwa-just-updated', '1'); } catch {}
    // Persist the current remote release version so useAppReleaseCheck
    // doesn't re-trigger the banner on the next load.
    try {
      const { markCurrentReleaseInstalled } = await import('@/hooks/useAppReleaseCheck');
      await markCurrentReleaseInstalled();
    } catch { /* best-effort */ }
    let reg = registrationRef.current;

    // Safety net: if controllerchange doesn't fire within 3s, force a hard reload.
    const safetyReload = setTimeout(() => {
      if (!reloadingRef.current) {
        reloadingRef.current = true;
        window.location.reload();
      }
    }, 3000);

    try {
      // 1) Try to make sure we have an up-to-date registration with a waiting SW.
      if (reg) {
        try {
          await reg.update();
        } catch {
          // offline or transient — keep going with whatever state we have
        }
      } else if ('serviceWorker' in navigator) {
        // No registration captured yet (rare): try to grab one now.
        try {
          reg = (await navigator.serviceWorker.getRegistration()) || null;
          registrationRef.current = reg;
        } catch {
          /* ignore */
        }
      }

      // 2) If a waiting SW exists, tell it to take over. controllerchange → reload.
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        return;
      }

      // 3) No waiting SW — wipe caches so the next reload pulls fresh assets,
      //    then hard-reload. This is what fixes desktop browsers where the banner
      //    was triggered but no waiting worker was actually queued.
      if ('caches' in window) {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch {
          /* ignore */
        }
      }
      clearTimeout(safetyReload);
      if (!reloadingRef.current) {
        reloadingRef.current = true;
        window.location.reload();
      }
    } catch {
      clearTimeout(safetyReload);
      if (!reloadingRef.current) {
        reloadingRef.current = true;
        window.location.reload();
      }
    }
  }, []);

  return (
    <PWAContext.Provider value={{ isInstallable, isInstalled, updateAvailable, setUpdateAvailable, installApp, updateApp }}>
      {children}
    </PWAContext.Provider>
  );
}

export function usePWAContext(): PWAContextValue {
  const ctx = useContext(PWAContext);
  if (!ctx) throw new Error('usePWAContext must be used within <PWAProvider>');
  return ctx;
}
